import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Modal, Select, DatePicker } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, FileTextOutlined, InboxOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  /* Force all text to be visible */
  button {
    color: ${COLORS.textPrimary} !important;
  }
  
  button span {
    color: ${COLORS.textPrimary} !important;
  }
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const Invoices: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [markPaidModalVisible, setMarkPaidModalVisible] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, pagination.current, pagination.pageSize, statusFilter, dateRange]);

  const fetchCompanies = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_COMPANIES || '/api/Administrator/companies');
      console.log('Companies response:', response.data);
      
      if (response.data?.status === 'success' && response.data?.data) {
        let companiesList: any[] = [];
        
        // Handle paginated response format: { itemsList, paginator }
        if (response.data.data.itemsList && Array.isArray(response.data.data.itemsList)) {
          companiesList = response.data.data.itemsList;
        }
        // Handle various other response structures
        else if (Array.isArray(response.data.data)) {
          companiesList = response.data.data;
        } else if (response.data.data.companies && Array.isArray(response.data.data.companies)) {
          companiesList = response.data.data.companies;
        } else if (response.data.data.rows && Array.isArray(response.data.data.rows)) {
          companiesList = response.data.data.rows;
        }
        
        console.log('Fetched companies:', companiesList.length);
        setCompanies(companiesList);
      } else {
        console.warn('Unexpected response structure:', response.data);
        // Try to extract companies from response data directly
        if (Array.isArray(response.data)) {
          setCompanies(response.data);
          console.log('Set companies from direct array');
        }
      }
    } catch (error: any) {
      console.error('Failed to fetch companies:', error);
      message.error('Failed to fetch merchant companies');
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        pageId: pagination.current.toString(),
        perPage: pagination.pageSize.toString(),
      });

      let endpoint: string;
      
      if (selectedMerchant) {
        // Get invoices for specific merchant
        endpoint = `${API_ENDPOINTS.ADMIN_GET_MERCHANT_INVOICES || '/api/Administrator/merchants/{merchantId}/invoices'}`
          .replace('{merchantId}', selectedMerchant);
      } else {
        // Get all invoices via admin endpoint if available
        endpoint = API_ENDPOINTS.ADMIN_GET_ALL_INVOICES || '/api/Administrator/merchants/invoices';
      }

      const response = await apiClient.get(`${endpoint}?${params}`);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        // Support two shapes: { invoices, total } or { itemsList, paginator }
        let invoicesList: any[] = [];
        let totalCount = 0;

        if (Array.isArray(data.invoices)) {
          invoicesList = data.invoices;
          totalCount = data.total || invoicesList.length;
        } else if (Array.isArray(data.itemsList)) {
          invoicesList = data.itemsList;
          totalCount = data.paginator?.total || data.paginator?.itemCount || invoicesList.length;
        } else if (Array.isArray(data)) {
          invoicesList = data;
          totalCount = invoicesList.length;
        }
        
        // Apply filters
        if (statusFilter) {
          invoicesList = invoicesList.filter((inv: any) => inv.status === statusFilter);
        }
        
        if (dateRange && dateRange[0] && dateRange[1]) {
          invoicesList = invoicesList.filter((inv: any) => {
            const invoiceDate = dayjs(inv.createdAt);
            return invoiceDate.isAfter(dateRange[0].startOf('day')) && invoiceDate.isBefore(dateRange[1].endOf('day'));
          });
        }
        
        if (search) {
          const searchLower = search.toLowerCase();
          invoicesList = invoicesList.filter((inv: any) =>
            inv.invoiceNumber?.toLowerCase().includes(searchLower) ||
            inv.merchantCompany?.name?.toLowerCase().includes(searchLower)
          );
        }

        setInvoices(invoicesList);
        setPagination(prev => ({
          ...prev,
          total: totalCount,
        }));
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch invoices');
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, pagination.current, pagination.pageSize, statusFilter, dateRange, search]);

  const handleViewInvoice = async (invoice: any) => {
    try {
      const endpoint = `${API_ENDPOINTS.ADMIN_GET_MERCHANT_INVOICE || '/api/Administrator/merchants/{merchantId}/invoices/{invoiceId}'}`
        .replace('{merchantId}', invoice.merchantCompanyId)
        .replace('{invoiceId}', invoice.id);

      const response = await apiClient.get(endpoint);
      
      if (response.data?.status === 'success' && response.data?.data) {
        setSelectedInvoice(response.data.data);
        setViewModalVisible(true);
      }
    } catch (error: any) {
      message.error('Failed to fetch invoice details');
      console.error('Error fetching invoice:', error);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;

    try {
      const endpoint = `${API_ENDPOINTS.ADMIN_MARK_INVOICE_PAID || '/api/Administrator/merchants/{merchantId}/invoices/{invoiceId}/mark-paid'}`
        .replace('{merchantId}', selectedInvoice.merchantCompanyId)
        .replace('{invoiceId}', selectedInvoice.id);

      await apiClient.post(endpoint, {
        paymentMethod: 'CREDIT_BALANCE',
      });
      message.success('Invoice marked as paid successfully');
      setMarkPaidModalVisible(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to mark invoice as paid');
    }
  };

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      PAID: 'green',
      PENDING: 'orange',
      FAILED: 'red',
      CANCELLED: 'default',
      DRAFT: 'default',
    };
    return statusMap[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const iconMap: Record<string, any> = {
      PAID: <CheckCircleOutlined />,
      PENDING: <ClockCircleOutlined />,
      FAILED: <CloseCircleOutlined />,
      CANCELLED: <CloseCircleOutlined />,
      DRAFT: <FileTextOutlined />,
    };
    return iconMap[status] || <FileTextOutlined />;
  };

  const columns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (text: string) => <Text strong style={{ color: COLORS.accent }}>{text}</Text>,
    },
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_: any, record: any) => (
        <Text style={{ color: COLORS.textPrimary }}>
          {record.merchantCompany?.name || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: any) => {
        const usdAmount = `$${parseFloat(record.total?.toString() || '0').toFixed(2)}`;
        const localCurrency = record.localCurrency;
        
        return (
          <div>
            <div style={{ fontWeight: 600, color: COLORS.textPrimary }}>{usdAmount} USD</div>
            {localCurrency && (
              <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                {localCurrency.localCurrencyAmount} ({localCurrency.localCurrency})
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: 'Paid At',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewInvoice(record)}
            style={{ color: COLORS.accent }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, margin: 0 }}>
          Invoices Management
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchInvoices}
            loading={loading}
            style={{ 
              color: COLORS.textPrimary, 
              borderColor: COLORS.border,
              backgroundColor: COLORS.bgSecondary 
            }}
          >
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
              Filter by Merchant (Optional):
            </Text>
            <Select
              placeholder="All merchants"
              style={{ width: '100%', maxWidth: 400 }}
              value={selectedMerchant}
              onChange={setSelectedMerchant}
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              optionLabelProp="label"
              options={companies.map(company => ({
                value: company.id,
                label: company.name || company.companyName || `Company ${company.id?.substring(0, 8)}`,
              }))}
              notFoundContent={
                <div style={{ textAlign: 'center', padding: '12px', color: COLORS.textSecondary }}>
                  <InboxOutlined style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                  No merchants found
                </div>
              }
            />
            {companies.length === 0 && (
              <Text style={{ color: COLORS.textSecondary, fontSize: 12, display: 'block', marginTop: 4 }}>
                No merchant companies registered yet
              </Text>
            )}
          </div>

          <>
            <div>
              <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
                Search:
              </Text>
              <Input
                placeholder="Search by invoice number or merchant name..."
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    maxWidth: 400,
                    backgroundColor: COLORS.bgPrimary,
                    borderColor: COLORS.border,
                    color: COLORS.textPrimary,
                  }}
                />
              </div>

              <Space wrap>
                <div>
                  <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
                    Status:
                  </Text>
                  <Select
                    placeholder="All Statuses"
                    style={{ width: 150 }}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    allowClear
                  >
                    <Select.Option value="PAID">Paid</Select.Option>
                    <Select.Option value="PENDING">Pending</Select.Option>
                    <Select.Option value="FAILED">Failed</Select.Option>
                    <Select.Option value="CANCELLED">Cancelled</Select.Option>
                    <Select.Option value="DRAFT">Draft</Select.Option>
                  </Select>
                </div>

                <div>
                  <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
                    Date Range:
                  </Text>
                  <RangePicker
                    value={dateRange ? [dateRange[0] as any, dateRange[1] as any] : null}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setDateRange([dayjs(dates[0].toDate()), dayjs(dates[1].toDate())]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                    format="YYYY-MM-DD"
                  />
                </div>
              </Space>
            </>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          backgroundColor: COLORS.bgSecondary,
          borderColor: COLORS.border,
        }}
        bodyStyle={{ padding: 0 }}
      >
        {!loading && invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <InboxOutlined style={{ fontSize: 48, color: COLORS.textSecondary, marginBottom: 16 }} />
            <div style={{ color: COLORS.textSecondary, fontSize: 16 }}>
                No invoices available for this merchant
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: 14, marginTop: 8 }}>
                Invoices will appear here once they are generated
              </div>
            </div>
          ) : (
            <TableScrollWrapper>
              <Table
                columns={columns}
                dataSource={invoices}
                rowKey="id"
                loading={loading}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} invoices`,
                  onChange: (page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                  },
                  style: { color: COLORS.textPrimary },
                }}
                style={{
                  backgroundColor: COLORS.bgSecondary,
                  color: COLORS.textPrimary,
                }}
                scroll={{ x: 'max-content' }}
              />
            </TableScrollWrapper>
          )}
        </Card>

      {/* View Invoice Modal */}
      <Modal
        title="Invoice Details"
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedInvoice(null);
        }}
        footer={
          <Space>
            {selectedInvoice?.status === 'PENDING' && (
              <Button
                key="markPaid"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setViewModalVisible(false);
                  setMarkPaidModalVisible(true);
                }}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Mark as Paid
              </Button>
            )}
            <Button key="close" onClick={() => {
              setViewModalVisible(false);
              setSelectedInvoice(null);
            }}>
              Close
            </Button>
          </Space>
        }
        width={800}
      >
        {selectedInvoice && (
          <div style={{ color: COLORS.textPrimary }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Invoice Number:</Text>
                <Text style={{ color: COLORS.accent, fontSize: 18, fontWeight: 600 }}>
                  {selectedInvoice.invoiceNumber}
                </Text>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Merchant:</Text>
                <Text>{selectedInvoice.merchantCompany?.name || 'N/A'}</Text>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Status:</Text>
                <Tag color={getStatusColor(selectedInvoice.status)} icon={getStatusIcon(selectedInvoice.status)}>
                  {selectedInvoice.status}
                </Tag>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Amount:</Text>
                <Text style={{ fontSize: 18, fontWeight: 600 }}>
                  ${parseFloat(selectedInvoice.total?.toString() || '0').toFixed(2)} USD
                </Text>
                {selectedInvoice.localCurrency && (
                  <Text style={{ color: COLORS.textSecondary, display: 'block', marginTop: 4 }}>
                    {selectedInvoice.localCurrency.localCurrencyAmount} ({selectedInvoice.localCurrency.localCurrency})
                  </Text>
                )}
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Due Date:</Text>
                <Text>{selectedInvoice.dueDate ? dayjs(selectedInvoice.dueDate).format('YYYY-MM-DD') : 'N/A'}</Text>
              </div>

              {selectedInvoice.paidAt && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Paid At:</Text>
                  <Text>{dayjs(selectedInvoice.paidAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
                </div>
              )}

              {selectedInvoice.billingPeriodStart && selectedInvoice.billingPeriodEnd && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Billing Period:</Text>
                  <Text>
                    {dayjs(selectedInvoice.billingPeriodStart).format('YYYY-MM-DD')} to{' '}
                    {dayjs(selectedInvoice.billingPeriodEnd).format('YYYY-MM-DD')}
                  </Text>
                </div>
              )}

              {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 12 }}>Line Items:</Text>
                  <Table
                    dataSource={selectedInvoice.lineItems}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      { title: 'Description', dataIndex: 'description', key: 'description' },
                      { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                      {
                        title: 'Unit Price',
                        dataIndex: 'unitPrice',
                        key: 'unitPrice',
                        render: (price: number) => `$${parseFloat(price?.toString() || '0').toFixed(2)}`,
                      },
                      {
                        title: 'Total',
                        dataIndex: 'total',
                        key: 'total',
                        render: (total: number) => `$${parseFloat(total?.toString() || '0').toFixed(2)}`,
                      },
                    ]}
                  />
                </div>
              )}

              {selectedInvoice.paidByAdmin && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Paid By Admin:</Text>
                  <Text>
                    {selectedInvoice.paidByAdmin.firstName} {selectedInvoice.paidByAdmin.lastName} ({selectedInvoice.paidByAdmin.email})
                  </Text>
                </div>
              )}

              {selectedInvoice.paidByMerchantUser && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Paid By Merchant:</Text>
                  <Text>
                    {selectedInvoice.paidByMerchantUser.firstName} {selectedInvoice.paidByMerchantUser.lastName} ({selectedInvoice.paidByMerchantUser.email})
                  </Text>
                </div>
              )}
            </Space>
          </div>
        )}
      </Modal>

      {/* Mark Paid Confirmation Modal */}
      <Modal
        title="Mark Invoice as Paid"
        open={markPaidModalVisible}
        onOk={handleMarkPaid}
        onCancel={() => {
          setMarkPaidModalVisible(false);
          setSelectedInvoice(null);
        }}
        okText="Mark as Paid"
        cancelText="Cancel"
      >
        <Space direction="vertical" size="small">
          <Text>
            Are you sure you want to mark invoice <Text strong>{selectedInvoice?.invoiceNumber}</Text> as paid?
          </Text>
          <Text type="warning" style={{ fontSize: '13px' }}>
            ⚠️ The invoice amount will be deducted from the merchant's credit balance.
          </Text>
        </Space>
      </Modal>
    </PageContainer>
  );
};

export default Invoices;
